import React, { useState, useEffect } from "react";
import AuthLayout from "./AuthLayout";

interface VerifyEmailPageProps {
  email?: string;
  onNavigate: (page: "login" | "dashboard") => void;
  onResendVerification?: () => Promise<void>;
}

const VerifyEmailPage: React.FC<VerifyEmailPageProps> = ({
  email,
  onNavigate,
  onResendVerification,
}) => {
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // Get token from URL
  const token = new URLSearchParams(window.location.search).get("token");

  // Verify email on load if token is present
  useEffect(() => {
    if (token) {
      verifyEmail();
    }
  }, [token]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const verifyEmail = async () => {
    if (!token) return;

    setVerifying(true);
    setError("");

    try {
      const response = await fetch(`/api/auth/verify-email?token=${token}`, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        setVerified(true);
      } else {
        const data = await response.json();
        setError(data.error || "VERIFICATION FAILED");
      }
    } catch (err) {
      setError("CONNECTION FAILED. TRY AGAIN.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    try {
      if (onResendVerification) {
        await onResendVerification();
      }
      setResendCooldown(60); // 60 second cooldown
    } catch (err) {
      setError("FAILED TO RESEND. TRY AGAIN.");
    }
  };

  // Verifying state
  if (verifying) {
    return (
      <AuthLayout title="*** VERIFYING EMAIL ***">
        <div className="space-y-6 text-center">
          <div className="text-green-500 text-4xl animate-pulse">...</div>
          <div className="text-green-400 text-sm">
            VERIFYING YOUR EMAIL ADDRESS
          </div>
          <div className="text-green-600 text-xs">PLEASE WAIT...</div>
        </div>
      </AuthLayout>
    );
  }

  // Verified state
  if (verified) {
    return (
      <AuthLayout title="*** EMAIL VERIFIED ***">
        <div className="space-y-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <div className="text-green-400 text-sm">
            EMAIL SUCCESSFULLY VERIFIED
          </div>
          <div className="text-green-600 text-xs">
            YOUR ACCOUNT IS NOW FULLY ACTIVATED.
          </div>
          <button
            onClick={() => onNavigate("dashboard")}
            className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm"
          >
            [CONTINUE TO DASHBOARD]
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Error state (with token)
  if (token && error) {
    return (
      <AuthLayout title="*** VERIFICATION FAILED ***">
        <div className="space-y-6 text-center">
          <div className="text-red-500 text-6xl mb-4">✕</div>
          <div className="text-red-400 text-sm">{error.toUpperCase()}</div>
          <div className="text-green-600 text-xs">
            THE VERIFICATION LINK MAY HAVE EXPIRED.
          </div>
          <button
            onClick={() => onNavigate("login")}
            className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm"
          >
            [RETURN TO LOGIN]
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Waiting for verification (no token - just signed up)
  return (
    <AuthLayout title="*** VERIFY YOUR EMAIL ***">
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-green-500 text-4xl mb-4">📧</div>
          <div className="text-green-400 text-sm mb-2">
            VERIFICATION EMAIL SENT TO:
          </div>
          {email && (
            <div className="text-green-500 font-bold text-lg mb-4">{email}</div>
          )}
        </div>

        <div className="border-2 border-green-700 p-4 space-y-2 text-green-600 text-sm">
          <p>PLEASE CHECK YOUR EMAIL AND CLICK THE VERIFICATION LINK.</p>
          <p className="text-green-700 text-xs">
            THE LINK WILL EXPIRE IN 24 HOURS.
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="border-2 border-red-500 bg-black p-3 text-center">
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Resend button */}
        <button
          onClick={handleResend}
          disabled={resendCooldown > 0}
          className="w-full py-3 border-2 border-green-700 text-green-500 hover:border-green-500 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resendCooldown > 0
            ? `[RESEND IN ${resendCooldown}s]`
            : "[RESEND VERIFICATION EMAIL]"}
        </button>

        {/* Back to login */}
        <button
          onClick={() => onNavigate("login")}
          className="w-full py-2 text-green-600 hover:text-green-400 text-sm"
        >
          [BACK TO LOGIN]
        </button>

        <div className="text-center text-green-700 text-xs">
          DIDN'T RECEIVE THE EMAIL? CHECK YOUR SPAM FOLDER.
        </div>
      </div>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
